import {getHostBridge} from "@frontend/hostContract"
import {activeThemeId} from "@frontend/themes/activeTheme"
import {
  type RecommendationHostPolicy,
  type RecommendationHostSource,
  gePartPurchaseHostPolicy,
  geRecommendationHostPolicy,
  getPartPurchaseHostSourceForAgentProfile,
  getRecommendationHostPolicyForAgentProfile,
} from "@ri2/app/ai/agent/geRecommendationHostPolicy"

import {emitHostAddedToCart, emitHostPartViewed} from "./hostJourneyEvents"

let hasWarnedMissingAddItemsToCart = false

/**
 * Build the cart item handed to the host. Writes the deprecated snake_case
 * oem twin alongside the camelCase contract so integrations built against
 * the original shape keep working through the deprecation window.
 * Does not include appliance model number — hosts should not receive it here.
 */
export const buildPartCartItem = ({
  id,
  oemNumber,
  name,
  price,
}: {
  id: number
  oemNumber: string
  name?: string
  price?: number
}): Ri2PartCartItem => ({
  id,
  oemNumber,
  ...(name !== undefined ? {name} : {}),
  ...(price !== undefined ? {price} : {}),
  oem_number: oemNumber,
})

/**
 * Emit `added_to_cart` first, then call the host callback. Order matters:
 * hosts that navigate synchronously (location.assign) would otherwise cancel
 * the event. Missing callback → warn once and return false so callers can
 * fall back to the catalog purchaseUrl.
 */
export const addItemsToCart = (
  items: Ri2PartCartItem[],
  caseId: string,
): boolean => {
  const first = items[0]
  const firstOemNumber = first?.oemNumber ?? first?.oem_number
  if (firstOemNumber) {
    emitHostAddedToCart(caseId, {
      oemNumber: firstOemNumber,
      partId: first.id,
      partName: first.name,
    })
  }

  const callback = getHostBridge()?.addItemsToCart
  if (!callback) {
    if (!hasWarnedMissingAddItemsToCart) {
      hasWarnedMissingAddItemsToCart = true
      console.warn(
        "[RepairIntelligence] addItemsToCart is not defined on the host bridge; falling back to catalog purchaseUrl when available.",
      )
    }
    return false
  }

  try {
    callback(items, caseId)
  } catch (error) {
    console.warn(
      "[RepairIntelligence] addItemsToCart threw; host cart handoff may have failed.",
      error,
    )
  }
  return true
}

/** Test-only: reset the missing-callback warning latch. */
export const resetAddItemsToCartWarningForTests = (): void => {
  hasWarnedMissingAddItemsToCart = false
}

/**
 * Bodewell always applies the GE-family brand gate, even when the server
 * agent profile is not `agent-ge`. Other themes keep the historical
 * fall-through (null policy → call host for every brand).
 */
const resolveRecommendationHostPolicy = (
  agentProfileId?: string | null,
): RecommendationHostPolicy | null =>
  getRecommendationHostPolicyForAgentProfile(agentProfileId) ??
  (activeThemeId === "bodewell" ? geRecommendationHostPolicy : null)

const resolvePartPurchaseHostSource = (
  agentProfileId?: string | null,
  brand?: string | null,
): RecommendationHostSource | null =>
  getPartPurchaseHostSourceForAgentProfile(agentProfileId, brand) ??
  (activeThemeId === "bodewell" ? gePartPurchaseHostPolicy(brand) : null)

/**
 * Navigate using the host's client-side router when available
 * (smooth SPA transition, no full page reload). Falls back to window.open
 * when running standalone or in the admin demo.
 *
 * Strips the origin from same-origin absolute URLs so router.push() receives
 * a relative path and works on localhost, QA, and production alike.
 *
 * Cross-origin URLs never reach `navigateTo`: a host router cannot push a
 * foreign origin, and the documented `history.pushState` implementation
 * throws a SecurityError on one.
 */
export const hostNavigate = (url: string): void => {
  let target = url
  try {
    const parsed = new URL(url, window.location.origin)
    if (parsed.origin !== window.location.origin) {
      window.open(parsed.toString(), "_blank", "noopener,noreferrer")
      return
    }
    target = parsed.pathname + parsed.search + parsed.hash
  } catch {
    // malformed URL -- use as-is
  }

  const navigateTo = getHostBridge()?.navigateTo
  if (navigateTo) {
    navigateTo(target)
  } else {
    window.open(target, "_blank", "noopener,noreferrer")
  }
}

export type PartPurchaseContext = {
  agentProfileId?: string | null
  brand?: string | null
  /** Case ID for Bodewell commerce host events. */
  caseId?: string
}

export const navigateToPart = (
  part: {
    id: number
    name: string | null
    manufacturerPartNumber: string
    purchaseUrl: string | null
  },
  context?: PartPurchaseContext,
): void => {
  const source = resolvePartPurchaseHostSource(
    context?.agentProfileId,
    context?.brand,
  )

  if (source === "internal") {
    if (part.purchaseUrl) {
      if (context?.caseId) {
        emitHostPartViewed(context.caseId, {
          oemNumber: part.manufacturerPartNumber,
          partId: part.id,
          partName: part.name,
        })
      }
      hostNavigate(part.purchaseUrl)
    }
    return
  }

  const hostUrl = getHostBridge()?.getPartDetailUrl?.({
    id: part.id,
    name: part.name ?? "",
    oemNumber: part.manufacturerPartNumber,
  })

  const url = hostUrl ?? part.purchaseUrl
  if (url) {
    if (context?.caseId) {
      emitHostPartViewed(context.caseId, {
        oemNumber: part.manufacturerPartNumber,
        partId: part.id,
        partName: part.name,
      })
    }
    hostNavigate(url)
  }
}

export const purchasePart = ({
  part,
  caseId,
  context,
  cartItem,
}: {
  part: {
    id: number
    name: string | null
    manufacturerPartNumber: string
    purchaseUrl: string | null
  }
  caseId: string
  context?: PartPurchaseContext
  cartItem: Ri2PartCartItem
}): "host-cart" | "catalog" | "none" => {
  const source = resolvePartPurchaseHostSource(
    context?.agentProfileId,
    context?.brand,
  )

  if (source === "internal") {
    if (!part.purchaseUrl) return "none"
    emitHostAddedToCart(caseId, {
      oemNumber: part.manufacturerPartNumber,
      partId: part.id,
      partName: part.name,
    })
    hostNavigate(part.purchaseUrl)
    return "catalog"
  }

  const invokedHost = addItemsToCart([cartItem], caseId)
  if (invokedHost) return "host-cart"

  // Host skipped addItemsToCart — degrade to catalog purchaseUrl when present.
  if (!part.purchaseUrl) return "none"
  hostNavigate(part.purchaseUrl)
  return "catalog"
}

/** Map internal remediation types to the tenant-facing recommendation enum. */
export const toRecommendationType = (
  remediationType: "diyRemediation" | "difmRemediation" | "replaceRemediation",
): RecommendationStepType => {
  switch (remediationType) {
    case "diyRemediation":
      return "diy"
    case "difmRemediation":
      return "proService"
    case "replaceRemediation":
      return "replace"
  }
}

/** Fields used to build CaseInfo from chat-collected case data. */
export type CaseInfoInput = {
  brand?: string | null
  modelNumber?: string | null
  applianceType?: string | null
  applianceAge?: string | null
  applianceAgeMonths?: number | null
  symptom?: string | null
  causes?: CaseCauseInfo[] | null
  parts?: CasePartInfo[] | null
}

/** @deprecated Use CaseInfoInput */
export type RecommendationProductInfo = CaseInfoInput

/** Build the reusable case snapshot passed into host callbacks. */
export const buildCaseInfo = ({
  caseId,
  product,
}: {
  caseId: string
  product: CaseInfoInput
}): CaseInfo => ({
  id: caseId,
  brand: product.brand ?? null,
  modelNumber: product.modelNumber ?? null,
  applianceType: product.applianceType ?? null,
  applianceAge: product.applianceAge ?? null,
  applianceAgeMonths: product.applianceAgeMonths ?? null,
  symptom: product.symptom ?? null,
  causes: product.causes ?? null,
  parts: product.parts ?? null,
})

/** Build the host callback context from the remediation CTA + case product info. */
export const buildRecommendationContext = ({
  remediationType,
  caseId,
  product,
}: {
  remediationType: "diyRemediation" | "difmRemediation" | "replaceRemediation"
  caseId: string
  product: CaseInfoInput
}): {type: RecommendationStepType; case: CaseInfo} => ({
  type: toRecommendationType(remediationType),
  case: buildCaseInfo({caseId, product}),
})

/**
 * Accept relative host paths (`/schedule`) and absolute http(s) URLs.
 * Reject empty strings, javascript: URIs, and free-form non-URL text.
 */
export const isUsableRecommendationUrl = (value: unknown): value is string => {
  if (typeof value !== "string") return false
  const trimmed = value.trim()
  if (!trimmed) return false
  if (trimmed.startsWith("/")) return true
  try {
    const parsed = new URL(trimmed)
    return parsed.protocol === "http:" || parsed.protocol === "https:"
  } catch {
    return false
  }
}

const resolveHostRecommendationUrl = (
  context: RecommendationContext,
): string | null => {
  const getRecommendationUrl = getHostBridge()?.getRecommendationUrl
  if (!getRecommendationUrl) return null

  let result: unknown
  try {
    result = getRecommendationUrl(context)
  } catch (error) {
    console.warn(
      "[RepairIntelligence] getRecommendationUrl threw; falling back to built-in flow.",
      error,
    )
    return null
  }

  if (result == null || (typeof result === "string" && !result.trim())) {
    return null
  }

  if (!isUsableRecommendationUrl(result)) {
    console.warn(
      "[RepairIntelligence] getRecommendationUrl returned an invalid URL; falling back to built-in flow.",
      result,
    )
    return null
  }

  return result.trim()
}

export type ResolveRecommendationUrlOptions = {
  agentProfileId?: string | null
}

export const resolveRecommendationUrl = (
  context: {type: RecommendationStepType; case: CaseInfo},
  options?: ResolveRecommendationUrlOptions,
): string | null => {
  const {type, case: caseInfo} = context
  if (type === "diy") {
    return null
  }

  const policy = resolveRecommendationHostPolicy(options?.agentProfileId)
  if (policy?.({type, brand: caseInfo.brand}) === "internal") {
    return null
  }

  return resolveHostRecommendationUrl({type, case: caseInfo})
}

/** Build the host callback context for replacement pricing. */
export const buildReplacementEstimateContext = ({
  caseId,
  product,
}: {
  caseId: string
  product: CaseInfoInput
}): ReplacementEstimateContext => ({
  case: buildCaseInfo({caseId, product}),
})

const HOST_REPLACEMENT_ESTIMATE_TIMEOUT_MS = 2000

export type HostReplacementEstimateStatus =
  | "ok"
  | "empty"
  | "invalid"
  | "error"
  | "timeout"
  | "skipped"

export type HostReplacementEstimateResolution = {
  estimate: ReplacementEstimate | null
  status: HostReplacementEstimateStatus
  error?: string
  durationMs: number
}

const isValidReplacementPrice = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value) && value > 0

const optionalTrimmedString = (value: unknown): string | null => {
  if (typeof value !== "string") return null
  const trimmed = value.trim()
  return trimmed === "" ? null : trimmed
}

const toReplacementEstimate = (result: unknown): ReplacementEstimate | null => {
  if (result == null) return null

  if (isValidReplacementPrice(result)) {
    return {price: result}
  }

  if (typeof result !== "object") return null

  const payload = result as ReplacementEstimate
  if (!isValidReplacementPrice(payload.price)) return null

  const replaceUrl = optionalTrimmedString(payload.replaceUrl)
  return {
    price: payload.price,
    replaceUrl:
      replaceUrl && isUsableRecommendationUrl(replaceUrl) ? replaceUrl : null,
    disclosure: optionalTrimmedString(payload.disclosure),
    modelNumber: optionalTrimmedString(payload.modelNumber),
    modelName: optionalTrimmedString(payload.modelName),
  }
}

/**
 * Ask the host for a replacement cost estimate.
 * Distinguishes missing callback, empty, invalid, error, and timeout so
 * callers can log the exchange without using the result for recommendation.
 */
export const resolveHostReplacementEstimate = async (
  context: ReplacementEstimateContext,
  timeoutMs = HOST_REPLACEMENT_ESTIMATE_TIMEOUT_MS,
): Promise<HostReplacementEstimateResolution> => {
  const callback = getHostBridge()?.getReplacementEstimate
  if (!callback) {
    return {estimate: null, status: "skipped", durationMs: 0}
  }

  const startedAt = Date.now()
  let timeoutId: ReturnType<typeof setTimeout> | undefined
  try {
    const result = await Promise.race([
      Promise.resolve().then(() => callback(context)),
      new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => reject(new Error("Timeout")), timeoutMs)
      }),
    ])

    const durationMs = Date.now() - startedAt
    const estimate = toReplacementEstimate(result)
    if (result == null) {
      return {estimate: null, status: "empty", durationMs}
    }
    if (!estimate) {
      console.warn(
        "[RepairIntelligence] getReplacementEstimate returned an invalid value; falling back to built-in bands.",
        result,
      )
      return {estimate: null, status: "invalid", durationMs, error: "invalid"}
    }
    return {estimate, status: "ok", durationMs}
  } catch (error) {
    const durationMs = Date.now() - startedAt
    const isTimeout = error instanceof Error && error.message === "Timeout"
    console.warn(
      "[RepairIntelligence] getReplacementEstimate error or timeout; falling back to built-in bands.",
      error,
    )
    return {
      estimate: null,
      status: isTimeout ? "timeout" : "error",
      durationMs,
      error: error instanceof Error ? error.message : String(error),
    }
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId)
  }
}

/**
 * Ask the host for a replacement cost estimate.
 * Returns null when the callback is missing, returns null/empty intentionally,
 * throws, times out, or returns an invalid value — never throws to the caller.
 */
export const getHostReplacementEstimate = async (
  context: ReplacementEstimateContext,
  timeoutMs = HOST_REPLACEMENT_ESTIMATE_TIMEOUT_MS,
): Promise<ReplacementEstimate | null> =>
  (await resolveHostReplacementEstimate(context, timeoutMs)).estimate

/** Convert a host dollar estimate into the cents-based Cost used by remediation UI. */
export const hostEstimateToReplaceCost = (
  estimate?: ReplacementEstimate | null,
): {cost: number; source: {type: "match"}} | null => {
  if (!estimate || !isValidReplacementPrice(estimate.price)) return null
  return {
    cost: Math.round(estimate.price * 100),
    source: {type: "match"},
  }
}

/** Shape accepted by peek/remediation tRPC inputs. */
export const toReplacementEstimateInput = (
  estimate?: ReplacementEstimate | null,
):
  | {
      price: number
      replaceUrl?: string | null
      disclosure?: string | null
      modelNumber?: string | null
      modelName?: string | null
    }
  | undefined => {
  if (!estimate || !isValidReplacementPrice(estimate.price)) return undefined
  return {
    price: estimate.price,
    replaceUrl: estimate.replaceUrl,
    disclosure: estimate.disclosure,
    modelNumber: estimate.modelNumber,
    modelName: estimate.modelName,
  }
}

type ProductInfoForCaseInfo = {
  brand?: {name?: string | null} | null
  modelNumber?: string | null
  productType?: {name?: string | null} | "unknown" | null
  applianceAge?: string | null
  symptomDescription?: {SymptomDescription?: string | null} | null
}

/** Map chat-collected product facts into the CaseInfoInput host snapshot. */
export const toCaseInfoInput = ({
  productInfo,
  causes,
  parts,
  applianceAgeMonths,
}: {
  productInfo: ProductInfoForCaseInfo
  causes?: CaseCauseInfo[] | null
  parts?: CasePartInfo[] | null
  applianceAgeMonths?: number | null
}): CaseInfoInput => ({
  brand: productInfo.brand?.name ?? null,
  modelNumber: productInfo.modelNumber ?? null,
  applianceType:
    productInfo.productType === "unknown"
      ? null
      : (productInfo.productType?.name ?? null),
  applianceAge: productInfo.applianceAge ?? null,
  applianceAgeMonths: applianceAgeMonths ?? null,
  symptom: productInfo.symptomDescription?.SymptomDescription ?? null,
  causes: causes ?? null,
  parts: parts ?? null,
})

type PendingHostPartPriceRequest = {
  item: Ri2PartPriceRequestItem
  resolvers: Array<(value: HostPartPriceData) => void>
}

const HOST_PRICE_BATCH_MS = 0

type HostPartPriceData = {
  price: number | null | undefined
  stockStatus: Ri2PartStockStatus
}

const emptyHostPartPriceData: HostPartPriceData = {
  price: undefined,
  stockStatus: undefined,
}

const hostPriceCache = new Map<string, HostPartPriceData>()
const inFlightPriceRequests = new Map<string, Promise<HostPartPriceData>>()
const queuedPriceRequests = new Map<string, PendingHostPartPriceRequest>()
let flushTimer: ReturnType<typeof setTimeout> | null = null

const normalizePriceKey = (oemNumber: string): string =>
  oemNumber.trim().toUpperCase()

const toHostPartPriceData = (
  value: Ri2PartPriceResponseValue,
): HostPartPriceData => {
  if (typeof value === "number" || value == null) {
    return {
      price: value,
      stockStatus: undefined,
    }
  }

  return {
    price: value.price,
    stockStatus:
      value.stockStatus ?? value.inventory_status ?? value.stock_status,
  }
}

const ensureFlushScheduled = (): void => {
  if (flushTimer) return

  flushTimer = setTimeout(() => {
    flushTimer = null
    void flushQueuedHostPriceRequests()
  }, HOST_PRICE_BATCH_MS)
}

const flushQueuedHostPriceRequests = async (): Promise<void> => {
  if (!queuedPriceRequests.size) return

  const queuedEntries = [...queuedPriceRequests.entries()]
  queuedPriceRequests.clear()

  const getPartPrices = getHostBridge()?.getPartPrices

  // No host function means no price source of truth is available.
  if (!getPartPrices) {
    for (const [key, request] of queuedEntries) {
      request.resolvers.forEach((resolve) => resolve(emptyHostPartPriceData))
      inFlightPriceRequests.delete(key)
    }
    return
  }

  try {
    const items = queuedEntries.map(([, request]) => request.item)
    const partDataByOemNumber = await getPartPrices(items)

    for (const [key, request] of queuedEntries) {
      const partData = toHostPartPriceData(partDataByOemNumber?.[key])
      hostPriceCache.set(key, partData)
      request.resolvers.forEach((resolve) => resolve(partData))
      inFlightPriceRequests.delete(key)
    }
  } catch {
    for (const [key, request] of queuedEntries) {
      request.resolvers.forEach((resolve) => resolve(emptyHostPartPriceData))
      inFlightPriceRequests.delete(key)
    }
  }
}

/**
 * Build the price-request item handed to the host. Writes the deprecated
 * snake_case keys alongside the camelCase contract so integrations built
 * against the original shape keep working through the deprecation window.
 */
export const toPartPriceRequestItem = ({
  oemNumber,
  manufacturerName,
}: {
  oemNumber: string
  manufacturerName: string
}): Ri2PartPriceRequestItem => {
  const normalized = normalizePriceKey(oemNumber)
  return {
    oemNumber: normalized,
    manufacturerName,
    oem_number: normalized,
    manufacturer_name: manufacturerName,
  }
}

const requestItemOemNumber = (item: Ri2PartPriceRequestItem): string =>
  normalizePriceKey(item.oemNumber ?? item.oem_number)

export const getHostPartPriceAndStockStatus = async (
  item: Ri2PartPriceRequestItem,
): Promise<HostPartPriceData> => {
  const key = requestItemOemNumber(item)
  if (!key) return emptyHostPartPriceData

  if (hostPriceCache.has(key)) {
    return hostPriceCache.get(key) ?? emptyHostPartPriceData
  }

  const inFlight = inFlightPriceRequests.get(key)
  if (inFlight) {
    return inFlight
  }

  const pendingPromise = new Promise<HostPartPriceData>((resolve) => {
    const queued = queuedPriceRequests.get(key)
    if (queued) {
      queued.resolvers.push(resolve)
    } else {
      queuedPriceRequests.set(key, {
        item: {...item, oemNumber: key, oem_number: key},
        resolvers: [resolve],
      })
    }
    ensureFlushScheduled()
  })

  inFlightPriceRequests.set(key, pendingPromise)
  return pendingPromise
}

export const getHostPartPrice = async (
  item: Ri2PartPriceRequestItem,
): Promise<number | null | undefined> => {
  const data = await getHostPartPriceAndStockStatus(item)
  return data.price
}

export const getHostPartPrices = async (
  items: Ri2PartPriceRequestItem[],
): Promise<Ri2PartPricesMap> => {
  const entries = await Promise.all(
    items.map(async (item) => {
      const key = requestItemOemNumber(item)
      const partData = await getHostPartPriceAndStockStatus(item)
      return [key, partData] as const
    }),
  )

  return Object.fromEntries(entries)
}
